import { CircularLoader } from '@modules/common/components/CircularLoader';
import { SelectNewIntegrationTypeDrawer } from '@modules/integrationsV2/components/SelectNewIntegrationTypeDrawer';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import { SearchRounded } from '@mui/icons-material';
import { InputAdornment, Skeleton, Stack, TextField } from '@mui/material';
import {
  IntegrationDirection,
  IntegrationFormDataViewFragment,
} from 'generated/graphql';
import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import useGetIntegrationsFormData from 'v2/core/repository/edge-server/integrations/useGetIntegrationsFormData';
import { AddNewIntegrationDrawerContainer } from './DrawerIntegrationsCardContainer';

export const AddNewIntegrationDrawerContent = () => {
  const {
    data: integrationFormData = [],
    isLoading: isLoadingIntegrationFormData,
  } = useGetIntegrationsFormData({
    fetchPolicy: 'cache-first', // Because we have "cache-and-network" on the root component, we can use "cache-first" here.
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');

  const debouncedSearchTerm = useMemo(
    () =>
      debounce((searchTerm: string) => {
        setSearchTerm(searchTerm);
      }, 250),
    [],
  );

  const integrationTypeFilterInDrawer = useIntegrationsStore(
    ({ view }) => view.integrationTypeFilterInDrawer,
  );

  const integrationsToDisplay =
    useMemo((): IntegrationFormDataViewFragment[] => {
      const filteredIntegrations = integrationFormData.filter((integration) => {
        const isInboundIntegration =
          integration.direction === IntegrationDirection.Inbound;

        const isSelectedIntegrationType =
          integrationTypeFilterInDrawer === 'all' ||
          integration.integrationType === integrationTypeFilterInDrawer;

        return (
          integration.channel.displayName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) &&
          isInboundIntegration &&
          isSelectedIntegrationType
        );
      });

      if (integrationTypeFilterInDrawer !== 'all') {
        return filteredIntegrations.sort((a, b) =>
          a.channel.displayName.localeCompare(b.channel.displayName),
        );
      }

      const uniqueIntegrations: Record<
        string,
        IntegrationFormDataViewFragment
      > = {};

      for (const integration of filteredIntegrations) {
        uniqueIntegrations[integration.channel.displayName] = integration;
      }

      return Object.values(uniqueIntegrations).sort((a, b) =>
        a.channel.displayName.localeCompare(b.channel.displayName),
      );
    }, [integrationTypeFilterInDrawer, integrationFormData, searchTerm]);

  const onInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setInputValue(event.target.value);
    debouncedSearchTerm(event.target.value);
  };

  if (isLoadingIntegrationFormData) {
    return (
      <Stack px={4} pt={4}>
        <Skeleton height={50} variant="rectangular" />
        <CircularLoader sx={{ top: '50%', position: 'absolute' }} />
      </Stack>
    );
  }

  return (
    <>
      <SelectNewIntegrationTypeDrawer
        integrationFormData={integrationFormData}
      />
      <Stack spacing={4} p={4}>
        <TextField
          fullWidth
          value={inputValue}
          onChange={onInputChange}
          placeholder="Search from the vast array of integrations that Enterpret provides."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded
                  sx={{
                    color: 'text.secondary',
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
        <AddNewIntegrationDrawerContainer
          integrations={integrationsToDisplay}
          searchTerm={searchTerm}
        />
      </Stack>
    </>
  );
};
